import { useDispatch, useSelector } from 'react-redux'
import store, { AppDispatch, RootState } from '../../../store'
import LineMeasurement from './Components/LineMeasurement'
import { useEffect } from 'react'
import { SceneManager } from '../../../visualization/sceneManager'
import {
  AbstractMesh,
  BoundingBox,
  BoundingInfo,
  IMouseEvent,
  PointerEventTypes,
  Vector3,
} from '@babylonjs/core'
import { v4 as uuidv4 } from 'uuid'
import {
  addLineMeasurement,
  removeLineMeasurement,
  updateLineMeasurement,
} from '../../../actions/measurement'
import { equalsWithEpsilon } from '../../../utils/Common'
import { Line } from '@babylonjs/gui'
import {
  computeBoundingBox,
  isBimObjectMesh,
  isDoorMesh,
  isGridMesh,
  isRoofMesh,
  isWallMesh,
  isWindowMesh,
} from '../../../visualization/utils'

const snapPoint = (pickedPoint: Vector3, pickedMesh: AbstractMesh) => {
  const snappedPoint = pickedPoint.clone()

  if (isGridMesh(pickedMesh)) {
    const gridSettings = store.getState().construction.gridSettings
    const cellSize = gridSettings.snapToGrid ? gridSettings.cellSize : 1

    if (gridSettings.snapToGrid) {
      snappedPoint.x = Math.round(snappedPoint.x / cellSize) * cellSize
      snappedPoint.z = Math.round(snappedPoint.z / cellSize) * cellSize
    }
  } else if (isRoofMesh(pickedMesh)) {
  } else if (
    isWallMesh(pickedMesh) ||
    isWindowMesh(pickedMesh) ||
    isDoorMesh(pickedMesh) ||
    isBimObjectMesh(pickedMesh)
  ) {
    let boundingBox: BoundingBox
    if (isBimObjectMesh(pickedMesh)) {
      const bbox = computeBoundingBox(pickedMesh.getChildMeshes(), true)
      const boundingInfo = new BoundingInfo(
        bbox.min,
        bbox.max,
        pickedMesh.getWorldMatrix(),
      )
      boundingBox = boundingInfo.boundingBox
    } else {
      boundingBox = pickedMesh.getBoundingInfo().boundingBox
    }

    for (const corner of boundingBox.vectorsWorld) {
      if (pickedPoint.equalsWithEpsilon(corner, 0.1)) {
        snappedPoint.copyFrom(corner)
        break
      }
    }
  }

  return snappedPoint
}

const LineMeasurements = ({ isVisible }: { isVisible: boolean }) => {
  const dispatch = useDispatch<AppDispatch>()

  const lineMeasurements = useSelector(
    (state: RootState) => state.measurement.lineMeasurements,
  )

  // TODO: Maybe move this to FreeMeasurements
  useEffect(() => {
    const sceneManager = SceneManager.getInstance()
    const scene = sceneManager.getScene()
    const camera = sceneManager.getCamera()
    const engine = sceneManager.getEngine()
    if (!scene || !camera || !engine) return

    let measurementId: string | null = null

    const onPointerDown = (event: IMouseEvent) => {
      const measurementsUI = sceneManager.measurementsUI
      if (event.button !== 0 || !event.ctrlKey || !measurementsUI) return

      const { pickedMesh, pickedPoint } = scene.pick(
        scene.pointerX,
        scene.pointerY,
      )
      if (!pickedMesh || !pickedPoint) return

      sceneManager.setCameraLock(true)

      measurementId = uuidv4()

      const line = new Line('LineMeasurement')
      line.color = '#FF2E79'
      line.lineWidth = 4
      line.metadata = { id: measurementId }
      line.isVisible = false

      measurementsUI.addControl(line)

      const snappedPoint = snapPoint(pickedPoint, pickedMesh)
      dispatch(
        addLineMeasurement({
          id: measurementId,
          startPoint: {
            x: snappedPoint.x,
            y: snappedPoint.y,
            z: snappedPoint.z,
          },
          endPoint: {
            x: snappedPoint.x,
            y: snappedPoint.y,
            z: snappedPoint.z,
          },
          isVisible: false,
        }),
      )
    }

    const onPointerMove = () => {
      const measurementsUI = sceneManager.measurementsUI
      const lineMeasurementControl = measurementsUI?.getDescendants(
        true,
        (control): control is Line =>
          control instanceof Line &&
          control.name === 'LineMeasurement' &&
          control.metadata.id === measurementId,
      )[0] as Line | undefined
      if (!measurementId || !lineMeasurementControl) return

      const { pickedMesh, pickedPoint } = scene.pick(
        scene.pointerX,
        scene.pointerY,
      )
      if (!pickedMesh || !pickedPoint) return

      const snappedPoint = snapPoint(pickedPoint, pickedMesh)
      lineMeasurementControl.isVisible = true

      dispatch(
        updateLineMeasurement({
          id: measurementId,
          data: {
            endPoint: {
              x: snappedPoint.x,
              y: snappedPoint.y,
              z: snappedPoint.z,
            },
            isVisible: true,
          },
        }),
      )
    }

    const onPointerUp = (event: IMouseEvent) => {
      const measurementsUI = sceneManager.measurementsUI
      if (event.button !== 0 || !measurementsUI) return

      if (measurementId) {
        dispatch(removeLineMeasurement({ id: measurementId }))

        const lineMeasurementControl = measurementsUI?.getDescendants(
          true,
          (control): control is Line =>
            control instanceof Line &&
            control.name === 'LineMeasurement' &&
            control.metadata.id === measurementId,
        )[0] as Line | undefined

        lineMeasurementControl?.dispose()
      }

      measurementId = null
      sceneManager.setCameraLock(false)
    }

    const onPointerObservable = scene.onPointerObservable.add(
      ({ type, event }) => {
        switch (type) {
          case PointerEventTypes.POINTERDOWN:
            onPointerDown(event)
            break
          case PointerEventTypes.POINTERMOVE:
            onPointerMove()
            break
          case PointerEventTypes.POINTERUP:
            onPointerUp(event)
            break
        }
      },
    )

    return () => {
      onPointerObservable.remove()
    }
  }, [dispatch])

  return (
    <>
      {Object.values(lineMeasurements).map((measurement) => (
        <LineMeasurement
          key={measurement.id}
          measurement={measurement}
          isVisible={isVisible && measurement.isVisible}
        />
      ))}
    </>
  )
}

const FreeMeasurements = ({ isVisible }: { isVisible: boolean }) => {
  return (
    <>
      <LineMeasurements isVisible={isVisible} />
    </>
  )
}

export default FreeMeasurements
